/**
 * Utility functions for inventory management
 */

/**
 * Determines if an item is countable (can be tracked by whole number quantity)
 *
 * Countable items: fruits, vegetables, eggs, bottles, cans, packages
 * Not countable: spices, flour, sugar, oils, bulk items
 *
 * @param category - Food category from AI detection
 * @param name - Item name
 * @returns true if item can be counted (1, 2, 3...)
 */
export function isItemCountable(category: string, name: string): boolean {
  // Categories that are typically countable
  const countableCategories = [
    'Fruits',
    'Vegetables',
    'Dairy',
    'Beverages',
    'Meat',
    'Plant-Based Protein',
    'Packaged Foods',
    'Bread/Grains',
    'Leftovers',
  ];

  // Keywords that indicate non-countable items (even in countable categories)
  const notCountableKeywords = [
    'salt', 'pepper', 'sugar', 'flour', 'oil', 'olive oil', 'vegetable oil',
    'spice', 'seasoning', 'powder', 'extract', 'vanilla',
    'honey', 'syrup', 'jam', 'butter', 'margarine',
    'rice', 'pasta', 'quinoa', 'couscous', 'oats',
    // Add more as needed
  ];

  const nameLower = name.toLowerCase();

  // Check if item name contains any non-countable keywords
  if (notCountableKeywords.some(keyword => nameLower.includes(keyword))) {
    return false;
  }

  // Check if category is typically countable
  return countableCategories.includes(category);
}

/**
 * Checks if a recipe unit is count-based (not weight or volume)
 *
 * @param unit - Recipe unit string (e.g., "cups", "grams", "pieces")
 * @returns true if unit represents countable items
 */
export function isCountUnit(unit: string | undefined): boolean {
  if (!unit) return true; // No unit = count-based

  const countUnits = ['piece', 'pieces', 'item', 'items', 'whole', 'unit', 'units', ''];
  return countUnits.includes(unit.toLowerCase().trim());
}

/**
 * Validates and sanitizes a quantity value
 * Ensures it's a positive whole number
 *
 * @param value - Input quantity (could be string from UI)
 * @returns Valid quantity number or undefined
 */
export function sanitizeQuantity(value: string | number | undefined): number | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const num = typeof value === 'string' ? parseInt(value, 10) : value;

  // Must be a positive whole number
  if (isNaN(num) || num < 1 || !Number.isInteger(num)) {
    return undefined;
  }

  return num;
}

/**
 * Auto-detect the inventory category from an item name.
 *
 * Matching is whole-word, not substring. The substring version this replaces
 * mis-filed a large share of a live 233-row pantry, because a food name that
 * merely *contains* a keyword is usually a different food:
 *
 *   "champignons"  contained "ham"    -> Meat
 *   "teaspoon"     contained "tea"    -> Beverages
 *   "pork steaks"  contained "tea"    -> Beverages
 *   "grapeseed oil" contained "grape" -> Fruits
 *
 * A handful of entries are genuine stems ("strawberr" must reach
 * "strawberries"); those are listed in STEM_KEYWORDS and keep prefix matching.
 *
 * French is a first-class tier rather than an afterthought. Without it every
 * French name fell through to 'Other', and 'Other' is a member of
 * PANTRY_CATEGORIES — so an unrecognised French food was silently filed as
 * shelf-stable. 88 of those 233 rows were 'Other'.
 */

/** Entries that are prefixes of a family of words rather than words themselves. */
const STEM_KEYWORDS = new Set([
  'strawberr', 'blueberr', 'raspberr', 'blackberr', 'cranberr', 'anchov', 'cherr', 'oat',
]);

/**
 * Whole-word, accent-tolerant, plural-tolerant keyword test.
 * Stems keep an open-ended suffix; everything else must match a whole word.
 */
function hasKeyword(text: string, keyword: string): boolean {
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const suffix = STEM_KEYWORDS.has(keyword) ? '[a-zà-ÿ]*' : '(?:s|es|x)?';
  return new RegExp(`(^|[^a-zà-ÿ0-9])${escaped}${suffix}([^a-zà-ÿ]|$)`, 'i').test(text);
}

/**
 * Multi-word terms checked before the single-word tiers, so a compound is not
 * decided by whichever of its words happens to appear in an earlier list.
 * Matched with `includes` because they are already unambiguous phrases.
 */
const COMPOUND_RULES: Array<[string, string]> = [
  // Juices are drinks, not the fruit
  ['orange juice', 'Beverages'], ['apple juice', 'Beverages'],
  ['grape juice', 'Beverages'], ['cranberry juice', 'Beverages'],
  ['lemon juice', 'Beverages'], ['lime juice', 'Beverages'],
  ['jus de lime', 'Beverages'], ['jus de citron', 'Beverages'],
  ['jus de pomme', 'Beverages'], ['jus d\'orange', 'Beverages'],
  // Oils are condiments, not the plant they came from
  ['olive oil', 'Condiments'], ['vegetable oil', 'Condiments'],
  ['sesame oil', 'Condiments'], ['canola oil', 'Condiments'],
  ['grapeseed oil', 'Condiments'], ['sunflower oil', 'Condiments'],
  ['safflower oil', 'Condiments'], ['peanut oil', 'Condiments'],
  ["huile d'olive", 'Condiments'], ['huile végétale', 'Condiments'],
  ['huile de sésame', 'Condiments'], ['huile de canola', 'Condiments'],
  ['huile de tournesol', 'Condiments'], ['huile de pépins', 'Condiments'],
  // Pepper the spice vs. pepper the vegetable
  ['black pepper', 'Spices'], ['white pepper', 'Spices'],
  ['peppercorn', 'Spices'], ['poivre noir', 'Spices'], ['poivre blanc', 'Spices'],
  ['poivre du moulin', 'Spices'], ['grains de poivre', 'Spices'],
  // Ground meats — 'ground' alone also prefixes ground spices
  ['ground beef', 'Meat'], ['ground pork', 'Meat'], ['ground turkey', 'Meat'],
  ['ground chicken', 'Meat'], ['ground lamb', 'Meat'],
  ['viande hachée', 'Meat'], ['bœuf haché', 'Meat'], ['boeuf haché', 'Meat'],
  ['porc haché', 'Meat'], ['poulet haché', 'Meat'],
  // Ground spices
  ['ground cumin', 'Spices'], ['ground coriander', 'Spices'],
  ['ground ginger', 'Spices'], ['ground cinnamon', 'Spices'],
  ['cumin moulu', 'Spices'], ['coriandre moulue', 'Spices'],
  ['gingembre moulu', 'Spices'], ['cannelle moulue', 'Spices'],
  // Canned/packaged staples that are shelf-stable despite their contents
  ['coconut milk', 'Packaged Foods'], ['coconut cream', 'Packaged Foods'],
  ['lait de coco', 'Packaged Foods'], ['crème de coco', 'Packaged Foods'],
  ['en conserve', 'Packaged Foods'], ['canned', 'Packaged Foods'],
  // Sauces before the plant protein / vegetable tiers
  ['soy sauce', 'Condiments'], ['sauce soja', 'Condiments'],
  ['sauce soya', 'Condiments'], ['fish sauce', 'Condiments'],
  ['sauce de poisson', 'Condiments'], ['hot sauce', 'Condiments'],
  ['sauce piquante', 'Condiments'], ['tomato sauce', 'Condiments'],
  ['sauce tomate', 'Condiments'], ['soy milk', 'Beverages'],
  // Dairy compounds
  ['cream cheese', 'Dairy'], ['cottage cheese', 'Dairy'], ['sour cream', 'Dairy'],
  ['whipped cream', 'Dairy'], ['half and half', 'Dairy'],
  ['crème sûre', 'Dairy'], ['crème aigre', 'Dairy'], ['fromage à la crème', 'Dairy'],
  ['crème à cuisson', 'Dairy'], ['crème fraîche', 'Dairy'],
  // Vegetables that read as something else word by word
  ['sweet potato', 'Vegetables'], ['green bean', 'Vegetables'],
  ['pomme de terre', 'Vegetables'], ['patate douce', 'Vegetables'],
  // Powders are spices
  ['garlic powder', 'Spices'], ['onion powder', 'Spices'], ['chili powder', 'Spices'],
  ['poudre de chili', 'Spices'], ['chile powder', 'Spices'],
  // Miscellaneous
  ['granola bar', 'Packaged Foods'], ['popcorn', 'Packaged Foods'],
  ['black bean', 'Plant-Based Protein'], ['kidney bean', 'Plant-Based Protein'],
  ['pinto bean', 'Plant-Based Protein'], ['navy bean', 'Plant-Based Protein'],
  ['watermelon', 'Fruits'], ['peanut butter', 'Condiments'],
  ["beurre d'arachide", 'Condiments'], ['maple syrup', 'Condiments'],
  ["sirop d'érable", 'Condiments'],
];

/**
 * Single-word tiers, in priority order. Earlier tiers win, so anything that
 * could plausibly land in two lists must be resolved by COMPOUND_RULES above.
 */
const CATEGORY_TIERS: Array<[string, string[]]> = [
  ['Leftovers', [
    'leftover', 'repas préparé', 'repas préparés', 'plat préparé',
    'plats préparés', 'reste', 'restes',
  ]],
  ['Condiments', [
    'ketchup', 'mustard', 'mayo', 'mayonnaise', 'sauce', 'salsa', 'dressing',
    'vinegar', 'relish', 'sriracha', 'bbq', 'teriyaki', 'pesto', 'jam', 'jelly',
    'marmalade', 'syrup', 'honey', 'hummus', 'miso', 'tahini', 'chutney',
    // French
    'moutarde', 'vinaigre', 'vinaigrette', 'confiture', 'miel', 'trempette',
    'cornichon', 'marinade', 'huile', 'sirop', 'chapelure', 'oil',
  ]],
  ['Beverages', [
    'juice', 'water', 'soda', 'pop', 'beer', 'wine', 'coffee', 'tea', 'kombucha',
    'smoothie', 'lemonade', 'drink', 'beverage', 'sparkling', 'sherry', 'vermouth',
    'sake', 'saké', 'cider', 'pinot', 'merlot', 'chardonnay', 'cabernet',
    'rosé', 'prosecco', 'riesling', 'shiraz',
    // French
    'jus', 'eau', 'vin', 'bière', 'café', 'thé', 'boisson', 'limonade',
  ]],
  ['Plant-Based Protein', [
    'tofu', 'tempeh', 'edamame', 'chickpea', 'lentil', 'seitan',
    // French
    'pois chiche', 'lentille', 'haricot',
  ]],
  ['Fruits', [
    'apple', 'banana', 'orange', 'strawberr', 'grape', 'pineapple', 'peach',
    'lemon', 'lime', 'cherr', 'blueberr', 'raspberr', 'blackberr', 'cranberr',
    'berry', 'berries', 'mango', 'kiwi', 'pear', 'plum', 'melon', 'cantaloupe',
    'grapefruit', 'pomegranate', 'fig', 'apricot', 'nectarine', 'tangerine',
    'clementine', 'mandarin', 'papaya', 'coconut', 'fruit',
    // French
    'pomme', 'banane', 'fraise', 'framboise', 'bleuet', 'mûre', 'cerise',
    'poire', 'pêche', 'prune', 'raisin', 'citron', 'pamplemousse', 'ananas',
    'melon', 'abricot', 'canneberge', 'noix de coco',
  ]],
  ['Vegetables', [
    'carrot', 'broccoli', 'spinach', 'lettuce', 'tomato', 'potato', 'onion',
    'garlic', 'pepper', 'cucumber', 'zucchini', 'celery', 'corn', 'pea',
    'asparagus', 'cauliflower', 'cabbage', 'kale', 'arugula', 'radish', 'beet',
    'turnip', 'squash', 'eggplant', 'artichoke', 'leek', 'mushroom', 'scallion',
    'shallot', 'chive', 'cilantro', 'parsley', 'yam', 'bok choy', 'salad',
    'vegetable', 'sprout', 'jalapeno', 'jalapeño', 'habanero', 'chile', 'chili',
    'avocado',
    // French
    'carotte', 'brocoli', 'épinard', 'laitue', 'tomate', 'patate', 'oignon',
    'ail', 'poivron', 'concombre', 'courgette', 'céleri', 'maïs', 'chou',
    'radis', 'betterave', 'navet', 'courge', 'aubergine', 'poireau',
    'champignon', 'échalote', 'ciboulette', 'coriandre', 'persil', 'salade',
    'légume', 'pousse', 'luzerne', 'micropousse', 'piment', 'basilic', 'menthe',
    'avocat',
  ]],
  ['Dairy', [
    'milk', 'cheese', 'yogurt', 'yoghurt', 'butter', 'cream', 'egg',
    'mozzarella', 'cheddar', 'parmesan', 'brie', 'feta', 'ricotta', 'gouda',
    'swiss', 'buttermilk', 'oka',
    // French
    'lait', 'fromage', 'yogourt', 'beurre', 'crème', 'œuf', 'oeuf', 'margarine',
  ]],
  ['Meat', [
    'chicken', 'beef', 'pork', 'steak', 'ham', 'bacon', 'sausage', 'turkey',
    'lamb', 'veal', 'duck', 'fish', 'salmon', 'tuna', 'shrimp', 'prawn', 'cod',
    'tilapia', 'crab', 'lobster', 'scallop', 'clam', 'mussel', 'anchov',
    'sardine', 'trout', 'meat', 'walleye', 'fillet', 'chop', 'brisket',
    // French
    'poulet', 'bœuf', 'boeuf', 'porc', 'jambon', 'saucisse', 'dinde', 'agneau',
    'veau', 'canard', 'poisson', 'saumon', 'thon', 'crevette', 'morue', 'plie',
    'truite', 'pétoncle', 'moule', 'palourde', 'homard', 'viande', 'filet',
    'côtelette', 'doré', 'lardon',
  ]],
  ['Spices', [
    'salt', 'cinnamon', 'cumin', 'paprika', 'oregano', 'thyme', 'rosemary',
    'turmeric', 'ginger', 'nutmeg', 'clove', 'cayenne', 'curry', 'spice',
    'seasoning', 'cardamom', 'saffron', 'bay leaf', 'basil', 'mint', 'dill',
    'vanilla', 'vanille',
    // French
    'sel', 'poivre', 'cannelle', 'origan', 'thym', 'romarin', 'curcuma',
    'gingembre', 'muscade', 'girofle', 'épice', 'assaisonnement', 'cardamome',
    'safran', 'laurier',
  ]],
  ['Bread/Grains', [
    'bread', 'rice', 'pasta', 'noodle', 'cereal', 'oat', 'granola', 'tortilla',
    'bagel', 'muffin', 'croissant', 'pita', 'wrap', 'flour', 'quinoa',
    'couscous', 'cracker', 'baguette', 'roll', 'bun', 'gnocchi', 'focaccia',
    // French
    'pain', 'riz', 'pâte', 'nouille', 'céréale', 'avoine', 'farine', 'brioche',
    'craquelin', 'petit pain',
  ]],
  ['Packaged Foods', [
    'chip', 'cookie', 'candy', 'chocolate', 'snack', 'bar', 'pretzel', 'nut',
    'almond', 'cashew', 'peanut', 'walnut', 'pistachio', 'frozen', 'instant',
    'chia', 'seed', 'sugar', 'molasses', 'cornstarch',
    // French
    'croustille', 'biscuit', 'bonbon', 'chocolat', 'collation', 'noix',
    'amande', 'arachide', 'surgelé', 'congelé', 'graine', 'sucre', 'cassonade',
    'mélasse', 'fécule',
  ]],
];

export interface DetectedCategory {
  category: string;
  /**
   * False when nothing matched and `category` is the 'Other' fallback. Callers
   * must not treat that as a real answer — 'Other' maps to the pantry shelf,
   * so a silent fallback is how a fish fillet ends up next to the flour.
   */
  confident: boolean;
}

/**
 * Detect the category and say whether the answer is real.
 *
 * @param name - the item name, in either supported language
 */
export function detectCategoryConfident(name: string): DetectedCategory {
  const n = (name || '').toLowerCase().trim();
  if (!n) return { category: 'Other', confident: false };

  for (const [term, category] of COMPOUND_RULES) {
    if (n.includes(term)) return { category, confident: true };
  }

  for (const [category, keywords] of CATEGORY_TIERS) {
    if (keywords.some((keyword) => hasKeyword(n, keyword))) {
      return { category, confident: true };
    }
  }

  return { category: 'Other', confident: false };
}

/**
 * Best-guess category string matching AI detection categories.
 *
 * @param name - The item name entered by the user
 * @returns Category name; 'Other' when nothing matched. Prefer
 *   `detectCategoryConfident` when you need to tell a real answer from the
 *   fallback.
 */
export function detectCategoryFromName(name: string): string {
  return detectCategoryConfident(name).category;
}
